import { html, nothing } from "lit";
import type { FileEntry, FileReadResult, FilesListResult } from "../controllers/files.ts";
import { t } from "../i18n.ts";

export type FilesProps = {
  connected: boolean;
  currentPath: string;
  loading: boolean;
  error: string | null;
  list: FilesListResult | null;
  showHidden: boolean;
  openFile: FileReadResult | null;
  openFileLoading: boolean;
  openFileError: string | null;
  editDraft: string | null;
  editSaving: boolean;
  onNavigate: (path: string) => void;
  onOpenFile: (path: string) => void;
  onCloseFile: () => void;
  onEditDraftChange: (content: string) => void;
  onSave: () => void;
  onDelete: (path: string) => void;
  onToggleHidden: () => void;
  onRefresh: () => void;
};

function getQuickPaths() {
  return [
    { label: t("files.home"), path: "/home/node" },
    { label: t("files.nabe_config"), path: "/home/node/.nabe" },
    { label: t("files.app_files"), path: "/app" },
    { label: t("files.extensions"), path: "/app/extensions" },
    { label: t("files.temp"), path: "/tmp" },
    { label: t("files.root"), path: "/" },
  ];
}

function fileIcon(entry: FileEntry): string {
  if (entry.type === "directory") return "📁";
  if (entry.type === "symlink") return "🔗";
  const ext = entry.name.split(".").pop()?.toLowerCase() ?? "";
  if (["json", "json5", "yaml", "yml", "toml", "xml"].includes(ext)) return "⚙️";
  if (["js", "ts", "mjs", "mts", "tsx", "jsx", "py", "rb", "go", "rs"].includes(ext)) return "📝";
  if (["md", "txt", "log", "csv"].includes(ext)) return "📄";
  if (["sh", "bash", "zsh"].includes(ext)) return "⚡";
  if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext)) return "🖼️";
  if (["zip", "tar", "gz", "bz2", "xz"].includes(ext)) return "📦";
  return "📄";
}

function formatDate(ms: number | null): string {
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function renderBreadcrumbs(currentPath: string, onNavigate: (p: string) => void) {
  const parts = currentPath.split("/").filter(Boolean);
  const crumbs: Array<{ label: string; path: string }> = [{ label: "/", path: "/" }];
  let accumulated = "";
  for (const part of parts) {
    accumulated += `/${part}`;
    crumbs.push({ label: part, path: accumulated });
  }

  return html`
    <div style="
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px;
      font-size: 14px;
      padding: 12px 0;
    ">
      ${crumbs.map(
        (crumb, i) => html`
          ${
            i > 0
              ? html`
                  <span style="color: var(--muted); margin: 0 2px">/</span>
                `
              : nothing
          }
          <button
            style="
              background: none;
              border: none;
              color: ${i === crumbs.length - 1 ? "var(--text-strong)" : "var(--accent)"};
              font-weight: ${i === crumbs.length - 1 ? "600" : "400"};
              cursor: pointer;
              padding: 4px 6px;
              border-radius: var(--radius-sm);
              font-size: 14px;
              transition: background 0.15s;
            "
            @click=${() => onNavigate(crumb.path)}
            @mouseover=${(e: Event) => {
              (e.target as HTMLElement).style.background = "var(--bg-hover)";
            }}
            @mouseout=${(e: Event) => {
              (e.target as HTMLElement).style.background = "none";
            }}
          >${crumb.label}</button>
        `,
      )}
    </div>
  `;
}

function renderFileRow(entry: FileEntry, props: FilesProps) {
  const isDir = entry.type === "directory";
  return html`
    <div
      style="
        display: grid;
        grid-template-columns: auto 1fr auto auto auto;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--border);
        cursor: pointer;
        transition: background 0.12s;
      "
      @click=${() => (isDir ? props.onNavigate(entry.path) : entry.isText ? props.onOpenFile(entry.path) : null)}
      @mouseover=${(e: Event) => {
        (e.currentTarget as HTMLElement).style.background = "var(--bg-hover)";
      }}
      @mouseout=${(e: Event) => {
        (e.currentTarget as HTMLElement).style.background = "transparent";
      }}
    >
      <span style="font-size: 18px; width: 24px; text-align: center;">${fileIcon(entry)}</span>
      <div style="min-width: 0;">
        <div style="
          font-size: 14px;
          font-weight: ${isDir ? "600" : "400"};
          color: ${isDir ? "var(--accent)" : "var(--text)"};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        ">${entry.name}${isDir ? "/" : ""}</div>
      </div>
      <span style="
        font-size: 12px;
        color: var(--muted);
        white-space: nowrap;
      ">${entry.sizeFormatted ?? ""}</span>
      <span style="
        font-size: 12px;
        color: var(--muted);
        white-space: nowrap;
        min-width: 120px;
        text-align: right;
      ">${formatDate(entry.modifiedAt)}</span>
      <button
        style="
          background: none;
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          color: var(--danger);
          padding: 4px 8px;
          font-size: 11px;
          cursor: pointer;
          opacity: 0.5;
          transition: opacity 0.15s;
        "
        title="Delete ${entry.name}"
        @click=${(e: Event) => {
          e.stopPropagation();
          if (confirm(`Delete "${entry.name}"?`)) {
            props.onDelete(entry.path);
          }
        }}
        @mouseover=${(e: Event) => {
          (e.target as HTMLElement).style.opacity = "1";
        }}
        @mouseout=${(e: Event) => {
          (e.target as HTMLElement).style.opacity = "0.5";
        }}
      >${t("files.delete")}</button>
    </div>
  `;
}

function renderFileEditor(props: FilesProps) {
  const file = props.openFile;
  if (!file) return nothing;

  const isDirty = props.editDraft !== null && props.editDraft !== file.content;

  return html`
    <div class="card" style="margin-top: 20px;">
      <!-- Editor header -->
      <div style="
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;
      ">
        <div>
          <div class="card-title" style="display: flex; align-items: center; gap: 8px;">
            <span>📝</span>
            <span>${file.name}</span>
            ${isDirty ? html`<span style="color: var(--warn); font-size: 12px; font-weight: 400;">(${t("files.unsaved")})</span>` : nothing}
          </div>
          <div class="card-sub">${file.path} — ${file.sizeFormatted}</div>
        </div>
        <div style="display: flex; gap: 10px;">
          <button
            class="btn primary"
            ?disabled=${!isDirty || props.editSaving}
            @click=${() => props.onSave()}
          >${props.editSaving ? t("files.saving") : t("files.save")}</button>
          <button
            class="btn"
            @click=${() => props.onCloseFile()}
          >${t("files.close")}</button>
        </div>
      </div>

      ${
        props.openFileError
          ? html`<div class="callout danger" style="margin-bottom: 12px;">${props.openFileError}</div>`
          : nothing
      }

      <!-- Editor -->
      <textarea
        style="
          width: 100%;
          min-height: 400px;
          max-height: 70vh;
          padding: 16px;
          border: 1.5px solid var(--border-strong);
          border-radius: var(--radius-md);
          background: var(--bg-accent);
          color: var(--text);
          font-family: var(--mono);
          font-size: 14px;
          line-height: 1.6;
          resize: vertical;
          outline: none;
          box-sizing: border-box;
          tab-size: 2;
          transition: border-color 0.2s;
        "
        .value=${props.editDraft ?? ""}
        @input=${(e: Event) => props.onEditDraftChange((e.target as HTMLTextAreaElement).value)}
        @focus=${(e: Event) => {
          (e.target as HTMLElement).style.borderColor = "var(--accent)";
        }}
        @blur=${(e: Event) => {
          (e.target as HTMLElement).style.borderColor = "var(--border-strong)";
        }}
        @keydown=${(e: KeyboardEvent) => {
          // Tab support
          if (e.key === "Tab") {
            e.preventDefault();
            const ta = e.target as HTMLTextAreaElement;
            const start = ta.selectionStart;
            const end = ta.selectionEnd;
            const val = ta.value;
            ta.value = val.substring(0, start) + "  " + val.substring(end);
            ta.selectionStart = ta.selectionEnd = start + 2;
            props.onEditDraftChange(ta.value);
          }
          // Cmd/Ctrl+S to save
          if ((e.metaKey || e.ctrlKey) && e.key === "s") {
            e.preventDefault();
            if (props.editDraft !== null && props.editDraft !== props.openFile?.content) {
              props.onSave();
            }
          }
        }}
      ></textarea>
    </div>
  `;
}

export function renderFiles(props: FilesProps) {
  return html`
    <!-- Quick Navigation -->
    <section class="card" style="margin-bottom: 20px;">
      <div class="card-title">${t("files.quick_access")}</div>
      <div class="card-sub">${t("files.quick_sub")}</div>
      <div style="
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 16px;
      ">
        ${getQuickPaths().map(
          (qp) => html`
            <button
              class="btn"
              style="font-size: 13px; padding: 8px 16px;
                ${props.currentPath === qp.path ? "border-color: var(--accent); color: var(--accent);" : ""}"
              ?disabled=${props.loading}
              @click=${() => props.onNavigate(qp.path)}
            >${qp.label}</button>
          `,
        )}
      </div>
    </section>

    <!-- File Browser -->
    <section class="card">
      <!-- Toolbar -->
      <div style="
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 12px;
      ">
        <div style="display: flex; align-items: center; gap: 8px;">
          <button
            class="btn"
            style="padding: 8px 12px; font-size: 18px; min-height: 36px;"
            ?disabled=${props.loading || !props.list}
            @click=${() => {
              if (props.list) props.onNavigate(props.list.parent);
            }}
            title="${t("files.go_up")}"
          >⬆️</button>
          <button
            class="btn"
            style="padding: 8px 14px; font-size: 13px;"
            ?disabled=${props.loading}
            @click=${() => props.onRefresh()}
            title="${t("files.refresh")}"
          >${t("files.refresh")}</button>
          <label style="
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 13px;
            color: var(--muted);
            cursor: pointer;
            padding: 6px 10px;
          ">
            <input
              type="checkbox"
              ?checked=${props.showHidden}
              @change=${() => props.onToggleHidden()}
              style="cursor: pointer; width: 16px; height: 16px;"
            />
            ${t("files.show_hidden")}
          </label>
        </div>
        <div style="
          font-size: 13px;
          color: var(--muted);
        ">
          ${props.list ? `${props.list.items.length} ${t("files.items")}` : ""}
        </div>
      </div>

      <!-- Breadcrumbs -->
      ${props.list ? renderBreadcrumbs(props.list.path, props.onNavigate) : nothing}

      ${
        !props.connected
          ? html`<div class="callout danger">${t("files.not_connected")}</div>`
          : nothing
      }

      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

      ${
        props.loading
          ? html`<div style="padding: 40px; text-align: center; color: var(--muted);">
              ${t("files.loading")}
            </div>`
          : nothing
      }

      <!-- File list -->
      ${
        props.list && !props.loading
          ? html`
            <div style="
              border: 1px solid var(--border);
              border-radius: var(--radius-md);
              overflow: hidden;
            ">
              <!-- Header -->
              <div style="
                display: grid;
                grid-template-columns: auto 1fr auto auto auto;
                align-items: center;
                gap: 12px;
                padding: 10px 16px;
                background: var(--bg-accent);
                border-bottom: 1px solid var(--border);
                font-size: 12px;
                font-weight: 600;
                color: var(--muted);
                text-transform: uppercase;
                letter-spacing: 0.04em;
              ">
                <span style="width: 24px;"></span>
                <span>${t("files.name")}</span>
                <span>${t("files.size")}</span>
                <span style="min-width: 120px; text-align: right;">${t("files.modified")}</span>
                <span style="width: 50px;"></span>
              </div>

              ${
                props.list.items.length === 0
                  ? html`<div style="
                      padding: 40px;
                      text-align: center;
                      color: var(--muted);
                      font-size: 14px;
                    ">${t("files.empty")}</div>`
                  : props.list.items.map((entry) => renderFileRow(entry, props))
              }
            </div>
          `
          : nothing
      }
    </section>

    <!-- File editor (when a file is open) -->
    ${
      props.openFileLoading
        ? html`<div class="card" style="margin-top: 20px; text-align: center; padding: 40px;">
          <div style="color: var(--muted);">${t("files.opening")}</div>
        </div>`
        : renderFileEditor(props)
    }
  `;
}
